import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "./_shared";
import { Button } from "@/components/ui/button";
import { jobDestination } from "@/components/job/jobNavigation";

// Project Control is the portfolio entry point. Job tools live in the same
// workspace reached from Jobs, with the selected project carried through.
const DESTINATIONS = {
  overview: ["Open job workspace", "overview"],
  daily: ["Site diary", "diary"],
  drawings: ["Drawing revisions", "drawings"],
  photos: ["Project photo library", "photos", "project-photos"],
  punch: ["Defects & follow-up", "punch"],
  tasks: ["Tasks & handover", "work"],
  signin: ["Site sign-in", "crew", "job-signin"],
  variations: ["Variations & billing", "commercial"],
  portal: ["Client portal", "communications"],
  budget: ["Costs & budget", "costs"],
};

export default function ProjectManagement() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [dash, setDash] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const activeJobId = params.get("job_id") || "";
  const activeJob = projects.find(job => job.id === activeJobId);
  const destination = (key) => {
    const [, tab, anchor] = DESTINATIONS[key];
    return jobDestination(activeJobId, tab, { anchor, advanced: true });
  };
  useEffect(() => {
    let current = true;
    api.get("/pm/projects").then(({ data }) => { if (current) setProjects(data.items || []); })
      .catch(() => { if (current) setError("Projects could not be loaded. Reload to try again."); })
      .finally(() => { if (current) setLoaded(true); });
    api.get("/pm/dashboard").then(({ data }) => { if (current) setDash(data); }).catch(() => {});
    api.get("/budget/portfolio").then(({ data }) => { if (current) setPortfolio(data); }).catch(() => {});
    return () => { current = false; };
  }, []);
  useEffect(() => {
    if (!loaded || error || activeJobId || !projects.length) return;
    const job = projects.find(item => item.status === "in_progress") || projects[0];
    const next = new URLSearchParams(params);
    next.set("job_id", job.id);
    setParams(next, { replace: true });
  }, [loaded, error, activeJobId, projects, params, setParams]);
  useEffect(() => {
    const mode = params.get("tab");
    if (activeJob && mode && DESTINATIONS[mode]) {
      const [, tab, anchor] = DESTINATIONS[mode];
      navigate(jobDestination(activeJob.id, tab, { anchor, advanced: true }), { replace: true });
    }
  }, [activeJob, params, navigate]);
  return <div className="p-4 lg:p-8 max-w-[1400px] mx-auto" data-testid="pm-page">
    <PageHeader title="Project control" subtitle="Review your projects, then open the same job workspace your team uses." />
    <section aria-label="Across all projects" className="mb-6"><h2 className="font-semibold text-sm mb-3">Across all projects</h2><div className="grid grid-cols-2 lg:grid-cols-5 gap-3">{[
      ["Diary entries today", dash?.logs_today], ["Open defects", dash?.punch_open], ["Drawings", dash?.drawings_total], ["Project photos", dash?.photos_total], ["Budgets needing review", portfolio?.filter(p => p.health !== "green").length],
    ].map(([label, value]) => <div key={label} className="rounded-lg border border-border bg-card p-3"><p className="text-sm text-muted-foreground">{label}</p><p className="text-xl font-semibold mt-1">{value ?? "—"}</p></div>)}</div></section>
    {error ? <p role="alert" className="text-sm text-red-700 dark:text-red-300">{error}</p> : !loaded ? <p role="status">Loading projects…</p> : !projects.length ? <div className="rounded-lg border border-border p-5"><p>No projects yet.</p><Button asChild className="mt-3"><Link to="/app/jobs/new">Create a job</Link></Button></div> : <section className="rounded-lg border border-border bg-card p-4 space-y-4" data-testid="pm-toolbar">
      <label className="block text-sm font-medium">Choose a job<select value={activeJobId} onChange={event => { const next = new URLSearchParams(params); next.set("job_id", event.target.value); next.delete("tab"); setParams(next); }} className="mt-2 block w-full min-h-11 rounded-md border border-border bg-background px-3" data-testid="pm-project-picker">{!activeJob && <option value={activeJobId}>{activeJobId ? "Selected job unavailable" : "Select a job"}</option>}{projects.map(job => <option key={job.id} value={job.id}>{job.job_number ? `${job.job_number} · ` : ""}{job.title}</option>)}</select></label>
      {activeJob && <><div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="font-semibold">{activeJob.title}</h2><p className="text-sm text-muted-foreground">Diary, resources, costs and files stay with this job.</p></div><Button asChild className="min-h-11 bg-[#FF5F15] hover:bg-[#E65100] text-white"><Link to={destination("overview")}>Open job workspace →</Link></Button></div><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">{Object.entries(DESTINATIONS).filter(([key]) => key !== "overview").map(([key, [label]]) => <Link key={key} to={destination(key)} className="flex items-center justify-between gap-2 min-h-11 border-b border-border py-2 text-sm hover:text-[#FF5F15]" data-testid={`pm-mode-${key}`}>{label}<span aria-hidden="true">→</span></Link>)}</div></>}
    </section>}
  </div>;
}
